import React, { useMemo } from "react";

/**
 * PWGDashboard
 *
 * Purpose:
 * - Landing "Command Center" view before drilling into cards/tables
 * - Uses the same props dataset already loaded in your app
 * - No external chart library required
 *
 * Expected prop shape (best effort / flexible):
 * - Tag
 * - Risk / Risk Profile
 * - Prop / Prop Type
 * - Confidence
 * - L5_Setup / "L5 Setup"
 * - GuruAlert / Guru Alert
 * - DvP / DvP Tier / Matchup / etc.
 * - Delta / Δ
 * - Player
 * - Team
 * - Opponent
 *
 * Usage example:
 * <PWGDashboard
 *   propsData={filteredProps}
 *   darkMode={darkMode}
 *   onOpenProps={() => setActiveView("props")}
 * />
 */

const CARD_RADIUS = 18;

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v) {
  return String(v ?? "").trim();
}

function upper(v) {
  return str(v).toUpperCase();
}

function getTag(row) {
  return upper(row?.Tag ?? row?.tag);
}

function getRisk(row) {
  return upper(
    row?.Risk ??
      row?.risk ??
      row?.["Risk"] ??
      row?.["Risk Tier"] ??
      ""
  );
}

function getPropType(row) {
  return str(
    row?.Prop ??
      row?.["Prop"] ??
      row?.["Prop Type"] ??
      row?.prop ??
      row?.propType ??
      ""
  );
}

function getConfidence(row) {
  return str(
    row?.Confidence ??
      row?.confidence ??
      row?.["Confidence"] ??
      ""
  );
}

function getL5Setup(row) {
  const raw = upper(
    row?.L5_Setup ??
      row?.["L5_Setup"] ??
      row?.["L5 Setup"] ??
      row?.l5Setup ??
      row?.L5Setup ??
      row?.l5Shape ??
      row?.L5_Shape ??
      row?.["L5 Shape"] ??
      row?.L5Shape ??
      ""
  );

  const map = {
    P5: "P5",
    "PERFECT 5": "P5",
    "L5: PERFECT 5": "P5",

    SURGE: "SURGE",
    "4/5 STRONG": "SURGE",
    "L5: 4/5 STRONG": "SURGE",

    BNC: "BNC",
    "BOUNCE SIGNAL": "BNC",
    "L5: BOUNCE SIGNAL": "BNC",

    MIX: "MIX",
    "3/5 MIXED": "MIX",
    "L5: 3/5 MIXED": "MIX",

    DIP: "DIP",
    "COOLING OFF": "DIP",
    "L5: COOLING OFF": "DIP",

    ICE: "ICE",
    "ICE COLD 0": "ICE",
    "0/5 WEAK": "ICE",
    "L5: ICE COLD 0": "ICE",

    RTS: "RTS",
    "RUN THEN SLIP": "RTS",

    STR: "STR",
    "SLIP THEN RUN": "STR",

    TURNING_UP: "TURNING_UP",
    "TURNING UP": "TURNING_UP",

    INCOMPLETE: "INCOMPLETE",
  };

  return map[raw] || raw || "UNKNOWN";
}

function getGuruAlert(row) {
  const raw = upper(
    row?.GuruAlert ??
      row?.guruAlert ??
      row?.["Guru Alert"] ??
      row?.["GuruAlert"] ??
      ""
  );

  // Flexible mapping
  if (["2", "ALERT", "ALERT ONLY", "CONTRARIAN", "CONTRARIAN EDGE"].includes(raw)) {
    return "ALERT";
  }
  if (["1", "WATCH", "WATCH ONLY"].includes(raw)) {
    return "WATCH";
  }
  return "NONE";
}

function getDvpTier(row) {
  const raw = upper(
    row?.DvP ??
      row?.dvp ??
      row?.["DvP"] ??
      row?.["Opp_DvP_Tier"] ??
      row?.dvpTier ??
      row?.DvpTier ??
      row?.Matchup ??
      ""
  );

  if (raw.includes("TARGET")) return "TARGET";
  if (raw.includes("SOFT")) return "SOFT";
  if (raw.includes("NEUTRAL")) return "NEUTRAL";
  if (raw.includes("TOUGH")) return "TOUGH";
  if (raw.includes("ELITE")) return "ELITE";
  if (raw.includes("R0")) return "R0";
  return raw || "UNKNOWN";
}

function getDelta(row) {
  return (
    toNum(
      row?.DELTA ??
        row?.Delta ??
        row?.delta ??
        row?.["Δ"] ??
        row?.["Delta"] ??
        row?.["Edge"] ??
        row?.edge
    ) ?? 0
  );
}

function getPlayer(row) {
  return str(row?.Player ?? row?.player ?? row?.Name ?? row?.name ?? "Unknown");
}

function getTeam(row) {
  return str(row?.Team ?? row?.team ?? "");
}

function getOpponent(row) {
  return str(row?.Opponent ?? row?.opponent ?? "");
}

function getTagBucket(tag) {
  if (tag === "MEGA SMASH") return "MEGA SMASH";
  if (tag === "SMASH") return "SMASH";
  if (tag === "LEAN OVER") return "LEAN OVER";
  if (tag === "LEAN UNDER") return "LEAN UNDER";
  if (tag === "MEGA FADE") return "MEGA FADE";
  if (tag === "FADE") return "FADE";
  if (tag === "COIN TOSS") return "COIN TOSS";
  return tag || "OTHER";
}

function getDirectionBucket(tag) {
  if (["MEGA SMASH", "SMASH", "LEAN OVER"].includes(tag)) return "OVERS";
  if (["MEGA FADE", "FADE", "LEAN UNDER"].includes(tag)) return "UNDERS/FADES";
  if (tag === "COIN TOSS") return "COIN TOSS";
  return "OTHER";
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function formatPct(part, total) {
  return `${pct(part, total)}%`;
}

function labelForL5(key) {
  const map = {
    P5: "Perfect 5",
    SURGE: "4/5 Strong",
    BNC: "Bounce",
    MIX: "Mixed",
    DIP: "Cooling Off",
    ICE: "Ice Cold",
    RTS: "Run Then Slip",
    STR: "Slip Then Run",
    TURNING_UP: "Turning Up",
    INCOMPLETE: "Incomplete",
    UNKNOWN: "Unknown",
  };
  return map[key] || key;
}

function MiniBar({
    label,
    value,
    max,
    color = "#2563eb",
    darkMode = false,
    suffix = "",
    onClick,
  }) {
    const width = max > 0 ? `${Math.max(6, Math.round((value / max) * 100))}%` : "0%";
  
    return (
      <button
        type="button"
        onClick={onClick}
        title={onClick ? "Click to open matching props" : ""}
        style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr 72px",
          gap: 10,
          alignItems: "center",
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: onClick ? "pointer" : "default",
          textAlign: "left",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: darkMode ? "#e5e7eb" : "#1f2937",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
  
        <div
          style={{
            height: 12,
            borderRadius: 999,
            background: darkMode ? "rgba(255,255,255,0.08)" : "#eef2f7",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width,
              borderRadius: 999,
              background: color,
              transition: "width 220ms ease",
            }}
          />
        </div>
  
        <div
          style={{
            textAlign: "right",
            fontSize: 13,
            fontWeight: 800,
            color: darkMode ? "#f3f4f6" : "#111827",
          }}
        >
          {value}{suffix}
        </div>
      </button>
    );
  }

function StatCard({ title, subtitle, children, darkMode = false }) {
  return (
    <div
      style={{
        background: darkMode ? "#0f172a" : "#ffffff",
        border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
        borderRadius: CARD_RADIUS,
        padding: 18,
        boxShadow: darkMode ? "none" : "0 12px 28px rgba(15,23,42,0.08)",
        minHeight: 180,
      }}
    >
      <div
        style={{
          fontWeight: 900,
          fontSize: 18,
          color: darkMode ? "#f9fafb" : "#0f172a",
          marginBottom: subtitle ? 4 : 12,
        }}
      >
        {title}
      </div>

      {subtitle ? (
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? "#94a3b8" : "#6b7280",
            marginBottom: 14,
          }}
        >
          {subtitle}
        </div>
      ) : null}

      {children}
    </div>
  );
}

function InsightBanner({ lines, darkMode = false }) {
  return (
    <div
      style={{
        background: darkMode ? "rgba(37,99,235,0.12)" : "rgba(37,99,235,0.06)",
        border: "1px solid rgba(37,99,235,0.18)",
        borderRadius: CARD_RADIUS,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 15,
          fontWeight: 900,
          color: darkMode ? "#dbeafe" : "#1d4ed8",
          marginBottom: 10,
        }}
      >
        PWG Insight Engine
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: darkMode ? "#e5e7eb" : "#1f2937",
            }}
          >
            • {line}
          </div>
        ))}
      </div>
    </div>
  );
}

function TopPlayRow({ row, rank, darkMode = false, onClick }) {
    const player = getPlayer(row);
    const prop = getPropType(row);
    const delta = getDelta(row);
    const tag = getTagBucket(getTag(row));
    const team = getTeam(row);
    const opp = getOpponent(row);
  
    return (
      <button
        type="button"
        onClick={onClick}
        title="Open player details"
        style={{
          display: "grid",
          gridTemplateColumns: "34px 1.4fr 1fr 90px 120px",
          gap: 10,
          alignItems: "center",
          padding: "10px 0",
          borderTop: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
          background: "transparent",
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "none",
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            fontWeight: 900,
            fontSize: 14,
            color: darkMode ? "#93c5fd" : "#2563eb",
          }}
        >
          #{rank}
        </div>
  
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 14,
              color: darkMode ? "#f8fafc" : "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {player}
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: darkMode ? "#94a3b8" : "#6b7280",
            }}
          >
            {team}{opp ? ` vs ${opp}` : ""}
          </div>
        </div>
  
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: darkMode ? "#e5e7eb" : "#334155",
          }}
        >
          {prop || "—"}
        </div>
  
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: delta >= 0 ? "#16a34a" : "#dc2626",
          }}
        >
          {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
        </div>
  
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 900,
              background:
                tag === "MEGA SMASH" ? "#0ea5e9" :
                tag === "SMASH" ? "#2e8b3c" :
                tag === "LEAN OVER" ? "#9333ea" :
                tag === "LEAN UNDER" ? "#f59e0b" :
                tag === "MEGA FADE" ? "#b91c1c" :
                tag === "FADE" ? "#ea580c" :
                "#94a3b8",
              color: "#fff",
            }}
          >
            {tag}
          </span>
        </div>
      </button>
    );
  }

export default function PWGDashboard({
    propsData = [],
    darkMode = false,
    onOpenProps,
    onDrillDown,
    onOpenTopPlay,
  }) {
  const rows = Array.isArray(propsData) ? propsData : [];

  const metrics = useMemo(() => {
    const total = rows.length;

    const directionCounts = {
      "OVERS": 0,
      "UNDERS/FADES": 0,
      "COIN TOSS": 0,
      "OTHER": 0,
    };

    const riskCounts = {
      LOW: 0,
      MODERATE: 0,
      HIGH: 0,
      OTHER: 0,
    };

    const tagCounts = {};
    const propTypeCounts = {};
    const l5Counts = {};
    const matchupCounts = {};
    const alertCounts = {
      ALERT: 0,
      WATCH: 0,
      NONE: 0,
    };

    const confidenceCounts = {};

    for (const row of rows) {
      const tag = getTagBucket(getTag(row));
      const direction = getDirectionBucket(tag);
      directionCounts[direction] = (directionCounts[direction] || 0) + 1;

      tagCounts[tag] = (tagCounts[tag] || 0) + 1;

      const risk = getRisk(row);
      if (risk.includes("LOW")) riskCounts.LOW += 1;
      else if (risk.includes("MODERATE")) riskCounts.MODERATE += 1;
      else if (risk.includes("HIGH")) riskCounts.HIGH += 1;
      else riskCounts.OTHER += 1;

      const propType = getPropType(row) || "Unknown";
      propTypeCounts[propType] = (propTypeCounts[propType] || 0) + 1;

      const l5 = getL5Setup(row) || "UNKNOWN";
      l5Counts[l5] = (l5Counts[l5] || 0) + 1;

      const dvp = getDvpTier(row) || "UNKNOWN";
      matchupCounts[dvp] = (matchupCounts[dvp] || 0) + 1;

      const guruAlert = getGuruAlert(row);
      alertCounts[guruAlert] = (alertCounts[guruAlert] || 0) + 1;

      const conf = getConfidence(row) || "Unknown";
      confidenceCounts[conf] = (confidenceCounts[conf] || 0) + 1;
    }

    const sortDesc = (obj) =>
      Object.entries(obj)
        .sort((a, b) => b[1] - a[1]);

    const topPropTypes = sortDesc(propTypeCounts).slice(0, 8);
    const topL5 = sortDesc(l5Counts).slice(0, 8);

    const topEdges = [...rows]
      .sort((a, b) => getDelta(b) - getDelta(a))
      .slice(0, 10);

    const bestPropType = topPropTypes[0]?.[0] || "—";
    const strongMomentumCount = (l5Counts.P5 || 0) + (l5Counts.SURGE || 0);
    const softMatchups = (matchupCounts.TARGET || 0) + (matchupCounts.SOFT || 0);
    const lowRiskCount = riskCounts.LOW || 0;

    return {
      total,
      directionCounts,
      riskCounts,
      tagCounts,
      propTypeCounts,
      topPropTypes,
      l5Counts,
      topL5,
      matchupCounts,
      alertCounts,
      confidenceCounts,
      topEdges,
      bestPropType,
      strongMomentumCount,
      softMatchups,
      lowRiskCount,
    };
  }, [rows]);

  const dirMax = Math.max(
    1,
    metrics.directionCounts["OVERS"],
    metrics.directionCounts["UNDERS/FADES"],
    metrics.directionCounts["COIN TOSS"]
  );

  const riskMax = Math.max(
    1,
    metrics.riskCounts.LOW,
    metrics.riskCounts.MODERATE,
    metrics.riskCounts.HIGH
  );

  const propMax = Math.max(1, ...metrics.topPropTypes.map(([, v]) => v), 1);
  const l5Max = Math.max(1, ...metrics.topL5.map(([, v]) => v), 1);
  const matchupMax = Math.max(
    1,
    metrics.matchupCounts.TARGET || 0,
    metrics.matchupCounts.SOFT || 0,
    metrics.matchupCounts.NEUTRAL || 0,
    metrics.matchupCounts.TOUGH || 0,
    metrics.matchupCounts.ELITE || 0,
    metrics.matchupCounts.R0 || 0
  );

  const insightLines = [
    `Best Prop Type: ${metrics.bestPropType}`,
    `Strong Momentum Plays: ${metrics.strongMomentumCount}`,
    `Target + Soft Matchups: ${metrics.softMatchups}`,
    `Low Risk Plays: ${metrics.lowRiskCount}`,
    `Guru Alerts: ${metrics.alertCounts.ALERT || 0}`,
  ];

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        padding: 4,
      }}
    >
      <InsightBanner lines={insightLines} darkMode={darkMode} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          gap: 16,
        }}
      >
        <div style={{ gridColumn: "span 6" }}>
          <StatCard
            title="Slate Distribution"
            subtitle={`Total Props: ${metrics.total}`}
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 12 }}>
                <MiniBar
                    label="Overs"
                    value={metrics.directionCounts["OVERS"]}
                    max={dirMax}
                    color="#22c55e"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.directionCounts["OVERS"], metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "tagDirection", value: "OVERS" })}
                />

                <MiniBar
                    label="Unders/Fades"
                    value={metrics.directionCounts["UNDERS/FADES"]}
                    max={dirMax}
                    color="#ef4444"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.directionCounts["UNDERS/FADES"], metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "tagDirection", value: "UNDERS/FADES" })}
                />

                <MiniBar
                    label="Coin Toss"
                    value={metrics.directionCounts["COIN TOSS"]}
                    max={dirMax}
                    color="#f59e0b"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.directionCounts["COIN TOSS"], metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "tagDirection", value: "COIN TOSS" })}
                />
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <StatCard
            title="Risk Distribution"
            subtitle="Where safety lives on the slate"
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 12 }}>
                <MiniBar
                    label="Low Risk"
                    value={metrics.riskCounts.LOW}
                    max={riskMax}
                    color="#10b981"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.riskCounts.LOW, metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "risk", value: "LOW" })}
                />

                <MiniBar
                    label="Moderate"
                    value={metrics.riskCounts.MODERATE}
                    max={riskMax}
                    color="#f59e0b"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.riskCounts.MODERATE, metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "risk", value: "MODERATE" })}
                />

                <MiniBar
                    label="High Risk"
                    value={metrics.riskCounts.HIGH}
                    max={riskMax}
                    color="#ef4444"
                    darkMode={darkMode}
                    suffix={` (${formatPct(metrics.riskCounts.HIGH, metrics.total)})`}
                    onClick={() => onDrillDown?.({ type: "risk", value: "HIGH" })}
                />  
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 7" }}>
          <StatCard
            title="Opportunity by Prop Type"
            subtitle="Where the strongest activity lives"
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 10 }}>
            {metrics.topPropTypes.map(([label, value]) => (
                <MiniBar
                    key={label}
                    label={label}
                    value={value}
                    max={propMax}
                    color="#2563eb"
                    darkMode={darkMode}
                    onClick={() => onDrillDown?.({ type: "propType", value: label })}
                />
            ))}
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 5" }}>
          <StatCard
            title="Guru Alerts"
            subtitle="Intelligence signals on the slate"
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 12 }}>
                <MiniBar
                    label="Alert"
                    value={metrics.alertCounts.ALERT}
                    max={Math.max(1, metrics.alertCounts.ALERT, metrics.alertCounts.WATCH, metrics.alertCounts.NONE)}
                    color="#dc2626"
                    darkMode={darkMode}
                    onClick={() => onDrillDown?.({ type: "guruAlert", value: "ALERT" })}
                />

                <MiniBar
                    label="Watch"
                    value={metrics.alertCounts.WATCH}
                    max={Math.max(1, metrics.alertCounts.ALERT, metrics.alertCounts.WATCH, metrics.alertCounts.NONE)}
                    color="#f59e0b"
                    darkMode={darkMode}
                    onClick={() => onDrillDown?.({ type: "guruAlert", value: "WATCH" })}
                />

                <MiniBar
                    label="None"
                    value={metrics.alertCounts.NONE}
                    max={Math.max(1, metrics.alertCounts.ALERT, metrics.alertCounts.WATCH, metrics.alertCounts.NONE)}
                    color="#94a3b8"
                    darkMode={darkMode}
                    onClick={() => onDrillDown?.({ type: "guruAlert", value: "NONE" })}
                />

              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  borderRadius: 14,
                  background: darkMode ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.06)",
                  border: "1px solid rgba(239,68,68,0.14)",
                  fontSize: 13,
                  fontWeight: 700,
                  color: darkMode ? "#fecaca" : "#991b1b",
                }}
              >
                {metrics.alertCounts.ALERT > 0
                  ? `⚡ ${metrics.alertCounts.ALERT} high-priority Guru Alerts detected.`
                  : "No high-priority Guru Alerts detected on this filtered slate."}
              </div>
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <StatCard
            title="L5 Momentum Map"
            subtitle="Recent form concentration"
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 10 }}>
            {metrics.topL5.map(([label, value]) => (
            <MiniBar
                key={label}
                label={labelForL5(label)}
                value={value}
                max={l5Max}
                color="#8b5cf6"
                darkMode={darkMode}
                onClick={() => onDrillDown?.({ type: "l5", value: label })}
            />
            ))}
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 6" }}>
          <StatCard
            title="Matchup Environment"
            subtitle="Defensive landscape"
            darkMode={darkMode}
          >
            <div style={{ display: "grid", gap: 10 }}>
            <MiniBar
            label="Target"
            value={metrics.matchupCounts.TARGET || 0}
            max={matchupMax}
            color="#16a34a"
            darkMode={darkMode}
            onClick={() => onDrillDown?.({ type: "dvp", value: "TARGET" })}
            />

            <MiniBar
            label="Soft"
            value={metrics.matchupCounts.SOFT || 0}
            max={matchupMax}
            color="#22c55e"
            darkMode={darkMode}
            onClick={() => onDrillDown?.({ type: "dvp", value: "SOFT" })}
            />

            <MiniBar
            label="Neutral"
            value={metrics.matchupCounts.NEUTRAL || 0}
            max={matchupMax}
            color="#94a3b8"
            darkMode={darkMode}
            onClick={() => onDrillDown?.({ type: "dvp", value: "NEUTRAL" })}
            />

            <MiniBar
            label="Tough"
            value={metrics.matchupCounts.TOUGH || 0}
            max={matchupMax}
            color="#f97316"
            darkMode={darkMode}
            onClick={() => onDrillDown?.({ type: "dvp", value: "TOUGH" })}
            />

            <MiniBar
            label="Elite"
            value={metrics.matchupCounts.ELITE || 0}
            max={matchupMax}
            color="#dc2626"
            darkMode={darkMode}
            onClick={() => onDrillDown?.({ type: "dvp", value: "ELITE" })}
            />
            </div>
          </StatCard>
        </div>

        <div style={{ gridColumn: "span 12" }}>
          <StatCard
            title="Top Edge Plays"
            subtitle="Highest delta opportunities in the current filtered pool"
            darkMode={darkMode}
          >
            <div style={{ display: "grid" }}>
              {metrics.topEdges.length === 0 ? (
                <div
                  style={{
                    padding: "8px 0",
                    fontSize: 14,
                    fontWeight: 700,
                    color: darkMode ? "#94a3b8" : "#6b7280",
                  }}
                >
                  No plays available for this view.
                </div>
              ) : (
                metrics.topEdges.map((row, idx) => (
                    <TopPlayRow
                        key={`${getPlayer(row)}-${getPropType(row)}-${idx}`}
                        row={row}
                        rank={idx + 1}
                        darkMode={darkMode}
                        onClick={() => onOpenTopPlay?.(row)}
                    />
                ))
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={() => onOpenProps?.()}
                style={{
                  borderRadius: 999,
                  padding: "10px 16px",
                  border: "1px solid rgba(37,99,235,0.25)",
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                View All Props →
              </button>
            </div>
          </StatCard>
        </div>
      </div>
    </div>
  );
}